import React, { useState, useCallback, useContext, useEffect } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { Text } from 'rebass'
import { BigNumber } from 'bignumber.js'

// utils
import { BIGNUMBER_FMT } from '../../../constants/bakery'
import { stopPropagation } from '../../../utils/func'
import { ArtworksProps } from '../../../constants/artwork'
import { SUPPORTTYPE  } from '../../../constants/bakery/nft'

// components
import { ButtonConfirmed } from '../../../components/Button'
import Loader from '../../../components/Loader'
import Player from '../../../components/nftInfo/Player'
import VoteModal from '../../BakeryArtworks/components/VoteModal'
import StakeBtn from '../../../components/nftInfo/button/StakeBtn'
import TransferBtn from '../../../components/nftInfo/button/TransferBtn'
import ArtworkBtn from '../../../components/nftInfo/button/ArtworkBtn'
import Row from '../../../components/Row'

// hooks
import { useActiveWeb3React } from '../../../hooks'
import useModal from '../../../hooks/bakery/useModal'
import useVoting from '../../../hooks/nft/useVoting'
import useApproveVote from '../../../hooks/nft/useApproveVote'
import useUserBids from '../../../hooks/nft/useUserBids'
import useUserAsks from '../../../hooks/nft/useUserAsks'
import useUserStakes from '../../../hooks/nft/useUserStakes'
import { userGameNftTypeV1V2 } from '../../../userType/userGameNftTypeV1V2'
import { NFTTYPE } from '../../../constants/bakery/nft'

const ArtworksCard = styled.div<{bg?: string}>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: ${({ bg, theme }) => bg ? bg : theme.bsBg15};
  border-radius: 28px;
  width: 276px;
  min-height: 513px;
  margin-bottom: 25px !important;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.bsBg27};
    box-shadow: 0px 2px 10px rgba(171, 133, 115, 0.16);
  }
  margin-right: 12px;
  margin-left: 12px;
`
const IconArtwork = styled.div<{bg?: string}>`
  display: block;
  width: 244px;
  height: 325px;
  border-radius: 18px;
  background-image: url(${({bg}) => (bg ? bg : '')});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
`
const CardHead = styled.div`
  position: relative;
  width: 100%;
`
const CardFoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`

const Title = styled.div<{color?:string}>`
  margin: 16px 0 20px 0;
  width: 100%;
  font-size: 16px;
  line-height: 22px;
  height: 44px;
  overflow-y: hidden;
  color: ${({ color,theme }) => color ? color : theme.bsText14};
`

const ArtworkLevel = styled.div`
  display: flex;
  margin-bottom: 16px;
  width: 100%;
  height: 17px;
  justify-content: space-between;
`
const Button = styled(ButtonConfirmed)`
  border: none;
  font-size: 14px;
  width: 244px;
  height: 40px;
  border-radius: 10px;
  padding: 0;
`
const Website = styled.a`
  width: 66px;
  line-height: 22px;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: underline;
  color: ${({ theme }) => theme.bsText14};
`
const BalanceBtn = styled(Row)<{isStaked?: boolean}>`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: ${({isStaked}) => (isStaked ? '0px' : '10px')};
  button {
    width: 47%;
    flex: 1 1 auto;
    margin-right: 16px;
  }
  button:last-child {
    margin-right: 0px;
  }
`

const PriceFlag = styled.div`
  position: absolute;
  top: 0px;
  left: -6px;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.bsBg8};
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  box-shadow: ${({ theme }) => theme.darkMode ? 'none' : '0px 2px 8px rgba(47, 20, 0, 0.74)' };
  color: ${({ theme }) => theme.bsText1};
  font-family: Baloo Da;
  font-weight: 400;
  max-width: 100%;
  height: 28px;
  padding: 0 8px;
  z-index: 1;
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -6px;
    border-width: 3px;
    border-style: solid;
    border-color: ${({ theme }) => theme.bsBg9} ${({ theme }) => theme.bsBg9} transparent transparent;
  }
`
const VoteFlag = styled(Button)`
  position: absolute;
  z-index: 1;
  top: 26px;
  right: 26px;
  width: auto;
  height: 28px;
  border-radius: 6px;
  font-family: Baloo Da;
  text-align: center;
  padding: 0 17px;
  white-space: nowrap;
  color: ${({ theme }) => theme.bsText7};
  background: ${({ theme }) => theme.bsText9};
  &:hover{
    background: ${({ theme }) => theme.bsText9};
  }
`
const VoteAutoRow = styled.div`
  flex-wrap: nowrap;
  justify-content: center;
  display:flex;
  align-items:center;
`
const linkExp = new RegExp(/^https?:\/\/([\w-]+\.)+[\w-]+.*$/)

export function Artworks({marketType, artwork, isMyArtwork, allowanceVote, status, canReview, history, allowanceSellAll, allowanceStake, type}: {marketType: string, artwork: ArtworksProps, isMyArtwork?: boolean, allowanceVote?: BigNumber, status: number, canReview: boolean, history: any, allowanceSellAll?: boolean, allowanceStake?: boolean, type?: string}) {
  const theme = useContext(ThemeContext)
  const { account } = useActiveWeb3React()

  // 获取我的竞价
  const [isGetIds, setIsGetIds] = useState(false)
  const getBids = useUserBids(marketType)
  const emptyBids: any[] = []
  const [ids, setIds] = useState(emptyBids)
  const getIds = useCallback(async() => {
    const bids = await getBids()
    if (bids.length) {
      const bidsId = bids.map((d: any) => {
        return d[0]
      })
      setIds(bidsId)
    }
    setIsGetIds(true)
  }, [getBids])

  // 获取我的挂单
  const getAsks = useUserAsks(marketType)
  const emptyAsks: any[] = []
  const [aksIds, setAskIds] = useState(emptyAsks)
  const getAskIds = useCallback(async() => {
    const asks = await getAsks()
    if (asks.length) {
      const asksId = asks.map((d: any) => {
        return d[0]
      })
      setAskIds(asksId)
    }
  }, [getAsks])

  // 获取我的stake
  const getStakes = useUserStakes(type)
  const getStakesV2 = useUserStakes(type + "_V2")

  const emptyStakes: any[] = []
  const [stakesIds, setStakesIds] = useState(emptyStakes)
   // 存v2的
  const getStakesIds = useCallback(async() => {
    const stakes = await getStakes()
        let stakesV2:any = []
        if(type && [
          NFTTYPE.DOGGIES,
          NFTTYPE.BARKING,
          NFTTYPE.EQUIPMENT,
          NFTTYPE.BAKEMUSKMIXER,
          NFTTYPE.CAR,
          NFTTYPE.COMBO,
          NFTTYPE.SOCCER,
          NFTTYPE.POKER,
        ].includes(type)){
          stakesV2 = await getStakesV2()
        }
        if (stakes.length || stakesV2.length) {
          const stakesId = stakes.map((d: any) => {
              return d
          })
          const stakesIdV2 = stakesV2.map((d: any) => {
            return d
          })
          setStakesIds([...stakesId,...stakesIdV2])
        } else {
          setStakesIds([])
        }
    }, [getStakes, getStakesV2])

  // 获取我的另一个版本的stake
  let gameType2:any = userGameNftTypeV1V2(type)
  const getStakes2 = useUserStakes(gameType2)
  const getStakes2_2 = useUserStakes(gameType2 + "_V2")

  const [stakesIds2, setStakesIds2] = useState(emptyStakes)
  const getStakesIds2 = useCallback(async () => {
    const stakes = await getStakes2()
      let stakesV2:any = []
      if([
        NFTTYPE.DOGGIES,
        NFTTYPE.BARKING,
        NFTTYPE.EQUIPMENT,
        NFTTYPE.BAKEMUSKMIXER,
        NFTTYPE.CAR,
        NFTTYPE.COMBO,
        NFTTYPE.SOCCER,
        NFTTYPE.POKER,
      ].includes(gameType2)){
        stakesV2 = await getStakes2_2()
      }
      if (stakes.length || stakesV2.length) {
        const stakesId = stakes.map((d: any) => {
            return d
        })
        const stakesIdV2 = stakesV2.map((d: any) => {
          return d
        })
        setStakesIds2([...stakesId,...stakesIdV2])
      } else {
        setStakesIds2([])
      }
    }, [getStakes2, getStakes2_2])

  useEffect(() => {
    if(gameType2){
      getStakesIds2()
    }
    getStakesIds()
  }, [artwork, getStakesIds, gameType2, getStakesIds2])

  useEffect(() => {
    if (account) {
      getAskIds()
      getIds()
    }
  }, [getBids, getIds, getAskIds, getAsks, account, artwork])

  //授权投票
  const [requestedApprovalVote, setRequestedApprovalVote] = useState(false)
  const onApproveVote = useApproveVote()
  const handleApproveVote = useCallback(async (event) => {
    stopPropagation(event)
    try {
      setRequestedApprovalVote(true)
      const txHash = await onApproveVote()
      if (!txHash) {
        setRequestedApprovalVote(false)
      }
    } catch (e) {
      console.log(e)
    }
  }, [onApproveVote, setRequestedApprovalVote])

  // 投票
  const {onVote} = useVoting(artwork.tokenId, marketType, artwork.owner) 
  const handleVote = useCallback(async (amount: string) => {
    try {
      const txHash = await onVote(amount)
      console.log('handleVote', txHash)
    } catch (e) {
      console.log(e)
    }
  }, [onVote])

  const handVote = ((event: any) => {
    stopPropagation(event)
    onVoting()
  })

  const [onVoting] = useModal(
    <VoteModal
      onConfirm={handleVote}
      tokenId={artwork.tokenId}
      name={artwork.name}
      nftType={artwork.nftType}
      nftId={artwork.nftId}
      type={marketType}
    />
  )

  return (
    <ArtworksCard className='fourItem' bg={artwork.level === 'v3' || isMyArtwork ? '' : '#DEDEDE'}>
      {
        true ? null :
        allowanceVote?.toNumber()
        ? 
        <VoteFlag onClick={handVote}>Vote</VoteFlag>
        : 
        <VoteFlag
          onClick={handleApproveVote}
          disabled={requestedApprovalVote}
          altDisabledStyle={requestedApprovalVote}
        >
          {
            requestedApprovalVote ? (
              <VoteAutoRow>
                Approving <Loader stroke="white" />
              </VoteAutoRow>
            ) : 'Approve Vote'
          }
        </VoteFlag>
      }
      {
        isGetIds && !ids.includes(artwork.tokenId) && Number(artwork.price) > 0 && <PriceFlag>
          <Text>{!artwork.auctionPrice ? 'On Sale' : 'On Auction'}</Text>
        </PriceFlag>
      }
      {
        artwork.level !== 'v1' && isGetIds && ids.includes(artwork.tokenId) && <PriceFlag>
          <Text>{Number(artwork.price) > 0 ? 'Offer made' : 'Order cancelled'}</Text>
        </PriceFlag>
      }
      {
        artwork.fileType === 3 ? (
          <Player history={history} player={artwork} isMyArtwork={isMyArtwork} status={status} canReview={canReview} type={marketType}></Player>
        ) : (
          <CardHead>
            <IconArtwork bg={artwork.fileUrl} />
          </CardHead>
        )
      }
      <CardFoot>
        {
          artwork.attributes.public_profile_link && linkExp.test(artwork.attributes.public_profile_link) ? (
            <Title style={{fontWeight: 'bold'}}  color={artwork.level === 'v3' || isMyArtwork ? '' : '#776D63'}>
              {artwork.name}
              <span>{ ' — ' }</span>
              <Website onClick={event => stopPropagation(event)} href={artwork.attributes.public_profile_link} target="_blank">{artwork.author}</Website>
            </Title>
          ) : (
            <Title color={artwork.level === 'v3' || isMyArtwork ? '' : '#776D63'}>
              {artwork.name}
              <span>{ ' — ' }</span>
              {'#' + artwork.tokenId}
            </Title>
          )
        }
        <ArtworkLevel>
          <Text fontSize={14} color={artwork.level === 'v3' || isMyArtwork ? theme.bsText14 : '#776D63'}>Price:</Text>
          <Text fontSize={14} fontWeight={700} color={artwork.level === 'v3' || isMyArtwork ? theme.bsText14 : '#776D63'}>{artwork.price ? (new BigNumber(!artwork.auctionPrice ? artwork.price : artwork.maxBidPrice).integerValue(BigNumber.ROUND_CEIL).toFormat(0, BIGNUMBER_FMT) + ' BAKE') : '-'}</Text>
        </ArtworkLevel>
        {
          !stakesIds.includes(artwork.tokenId) && !stakesIds2.includes(artwork.tokenId) ?
          <ArtworkBtn
            artwork={artwork}
            status={status}
            isMyArtwork={isMyArtwork}
            ids={ids}
            myAksIds={aksIds}
            marketType={marketType}
            symbol={SUPPORTTYPE.BAKE}
          />
          : null
        }
        <BalanceBtn
          isStaked={stakesIds2.includes(artwork.tokenId) || stakesIds.includes(artwork.tokenId)}
        >
          {
            isMyArtwork && !ids.includes(artwork.tokenId) && !stakesIds.includes(artwork.tokenId) && !stakesIds2.includes(artwork.tokenId) 
            ? <TransferBtn artwork={artwork} marketType={marketType} />
            : null
          }
          {
          type && isMyArtwork && !ids.includes(artwork.tokenId) 
            ? <StakeBtn artwork={artwork} gameType={type} />
            : null
          }
        </BalanceBtn>
      </CardFoot>
    </ArtworksCard>
  )
}
